import { z } from "zod";
import { ApproachSchema, ExperienceLevelSchema, ProfileEvidenceSchema, ProfileIntakeLineSchema, RehearsalPartsSchema } from "./learner.js";
import { RubricDimensionSchema } from "./primitives.js";
/**
 * THE ARRIVAL BOOK (2026-10-11, Andre): how a learner and AJ met, read back
 * as the record's first book. Everything in it was already kept at the time
 * except the rehearsal, which is kept from this release on
 * (`profile_rehearsals`). Assembly only: the confirmed intake's transcript,
 * the confirmed profile with the quotes each value came from, the first focus
 * in the learner's words, the one rehearsal move, and the papers line AJ said
 * when the desk was laid. Written once at confirmation and never rewritten; a
 * later correction of the profile settles a fresh row, and the book follows
 * the confirmed one.
 *
 * LAW 2: words only. LAW 5: exported and deleted with the coach's data; the
 * profile row itself stays (results hang off it) so the book goes when its
 * intake goes. LAW 6: the read-back is the human half of the baseline.
 */
/** The learner's own words behind one value of the read-back. */
export const ArrivalEvidenceSchema = ProfileEvidenceSchema;
export const ArrivalReadBackSchema = z.strictObject({
    name: z.string().min(1),
    experience_level: ExperienceLevelSchema,
    target_functions: z.array(z.string()),
    target_firms: z.array(z.string()),
    self_identified_gaps: z.array(z.string()),
    approaches: z.array(ApproachSchema).max(2),
    approaches_provisional: z.boolean(),
    /** Every quote the extractor verified, in the order it kept them; empty for a profile older than the intake (migration 0027). */
    evidence: z.array(ArrivalEvidenceSchema),
});
/** The one rehearsal move, as made: the authored scene, the learner's words, the three parts code verified, the authored reflection they read. */
export const ArrivalRehearsalSchema = z.strictObject({
    scene: z.string().min(1),
    move: z.string().min(1),
    parts: RehearsalPartsSchema,
    reflection: z.array(z.string()),
    madeAt: z.iso.datetime(),
});
/**
 * Why the rehearsal page is absent, when it is: the learner declined it, or
 * it was made before rehearsals were kept (every profile confirmed before
 * migration 0041 with `rehearsal_done_at` set and no row).
 */
export const ArrivalRehearsalAbsenceSchema = z.enum(["skipped", "not_kept"]);
export const ArrivalBookSchema = z.strictObject({
    /** When the learner confirmed the read-back: the intake's own `confirmed_at` (migration 0041), or the profile's for an intake confirmed before it was kept. */
    metAt: z.iso.datetime(),
    /** How we met: the confirmed intake's transcript, both sides, in order. The first coach line is the script's closing question. */
    conversation: z.array(ProfileIntakeLineSchema),
    readBack: ArrivalReadBackSchema,
    /** The first focus, laid on the desk from what the learner said they would work on first; null when they named none. */
    focus: z.strictObject({ dimension: RubricDimensionSchema, text: z.string().min(1) }).nullable(),
    rehearsal: ArrivalRehearsalSchema.nullable(),
    /** Set exactly when `rehearsal` is null and the learner has passed the rehearsal (made or declined). */
    rehearsalAbsence: ArrivalRehearsalAbsenceSchema.nullable(),
    /** The papers AJ said were on the desk, as said, and when. */
    papers: z.strictObject({ lines: z.array(z.string().min(1)), focusPlaced: z.boolean(), placedAt: z.iso.datetime() }),
});
//# sourceMappingURL=arrival.js.map